import type { UIMessage } from 'ai'
import { isArray, isPlainObject, isString } from 'lodash'

import {
  type Action,
  type AssistantMessageMetadata,
  type Conversation,
  type CursorPagedListConversation,
  type StoredUiMessage,
  enhancedApi
} from '.'

/**
 * What the generated assistant client cannot express: per-conversation and per-action cache tags (ids on the spec's
 * single `Assistant` tag, which no endpoint provides bare, so one write never refetches everything), the cursor-paged conversation history as an infinite query, seeding each
 * action's cache entry from the conversation detail, and narrower types for fields the spec leaves as strings or
 * open objects.
 */

/** `proposed` → `queued` → `running` → `done` | `failed`, or `proposed` → `rejected`. */
export type AssistantActionStatus = 'proposed' | 'rejected' | 'queued' | 'running' | 'done' | 'failed'

const ACTION_STATUSES: readonly AssistantActionStatus[] = [
  'proposed',
  'rejected',
  'queued',
  'running',
  'done',
  'failed'
]

export function readActionStatus(status: string | undefined): AssistantActionStatus | undefined {
  return ACTION_STATUSES.find((known) => known === status)
}

/**
 * A chat message with the metadata assistant-api types: the timing of a reply and the viewer's rating. A user
 * message also carries the entities tagged with `@` as `contextRefs`, which the spec leaves untyped.
 */
export type AssistantUIMessage = UIMessage<AssistantMessageMetadata & { contextRefs?: unknown }>

type StoredMessageBody = Omit<StoredUiMessage, 'metadata'>

/** The stored transcript, keeping the entries shaped like AI SDK UI messages. */
export function toUIMessages(messages: StoredUiMessage[]): AssistantUIMessage[] {
  return messages.flatMap(({ metadata, ...message }) =>
    isUIMessageBody(message) ? [{ ...message, ...(metadata ? { metadata } : {}) }] : []
  )
}

function isUIMessageBody(
  message: StoredMessageBody
): message is StoredMessageBody & Pick<AssistantUIMessage, 'role' | 'parts'> {
  const { id, role, parts } = message
  return (
    isString(id) &&
    (role === 'user' || role === 'assistant' || role === 'system') &&
    isArray(parts) &&
    parts.every((part) => isPlainObject(part) && isString((part as { type?: unknown }).type))
  )
}

const CONVERSATION_PAGE_SIZE = 20

const CONVERSATION_LIST = { type: 'Assistant', id: 'CONVERSATION_LIST' } as const

const conversationTag = (conversationId: string) => ({
  type: 'Assistant' as const,
  id: `conversation:${conversationId}`
})

const actionTag = (actionId: string) => ({ type: 'Assistant' as const, id: `action:${actionId}` })

const DASHBOARD_SAVES = { type: 'Assistant', id: 'DASHBOARD_SAVES' } as const

enhancedApi.enhanceEndpoints({
  endpoints: {
    createConversation: { invalidatesTags: [CONVERSATION_LIST] },
    listConversations: { providesTags: [CONVERSATION_LIST] },
    getConversation: {
      // `useChat` holds the live transcript while a thread is open, so a cached copy is stale by the time the
      // conversation is reopened.
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { conversationId }) => [conversationTag(conversationId)],
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled.catch(() => ({ data: undefined }))
        if (!data?.actions.length) return
        dispatch(
          enhancedApi.util.upsertQueryEntries(
            data.actions.map((action) => ({
              endpointName: 'getAction' as const,
              arg: { actionId: action.id },
              value: action
            }))
          )
        )
      }
    },
    updateConversation: { invalidatesTags: [CONVERSATION_LIST] },
    deleteConversation: {
      // Drops the row from every loaded page at once; the refetch the tag triggers then lands on the same list.
      onQueryStarted: async ({ conversationId }, { dispatch, queryFulfilled }) => {
        const deleted = await queryFulfilled.then(
          () => true,
          () => false
        )
        if (!deleted) return
        dispatch(
          pagedConversationsApi.util.updateQueryData('listConversationPages', undefined, (draft) => {
            for (const page of draft.pages) {
              page.data = page.data.filter((conversation) => conversation.id !== conversationId)
            }
          })
        )
      },
      invalidatesTags: [CONVERSATION_LIST]
    },
    shareConversation: { invalidatesTags: [CONVERSATION_LIST] },
    sendMessage: { invalidatesTags: [CONVERSATION_LIST] },
    setMessageFeedback: { invalidatesTags: [] },
    getAction: {
      providesTags: (_result, _error, { actionId }) => [actionTag(actionId)]
    },
    dispatchAction: {
      // The 202 carries the queued status; writing it into the cache hides the decision before the refetch lands.
      onQueryStarted: async ({ actionId }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled.catch(() => ({ data: undefined }))
        if (!data) return
        dispatch(
          enhancedApi.util.updateQueryData('getAction', { actionId }, (draft) => {
            draft.status = data.status
          })
        )
      },
      invalidatesTags: (_result, _error, { actionId }) => [actionTag(actionId)]
    },
    // A presigned link expires within minutes, so each download asks for a fresh one.
    getActionArtifactLink: { providesTags: [], keepUnusedDataFor: 0 },
    createAttachment: { invalidatesTags: [] },
    refreshDashboard: { invalidatesTags: [] },
    listDashboardSaves: { providesTags: [DASHBOARD_SAVES] },
    getDashboardSave: { providesTags: [] },
    createDashboardSave: { invalidatesTags: [DASHBOARD_SAVES] },
    deleteDashboardSave: { invalidatesTags: [DASHBOARD_SAVES] },
    rejectAction: {
      onQueryStarted: async ({ actionId }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled.catch(() => ({ data: undefined }))
        if (data) dispatch(enhancedApi.util.upsertQueryData('getAction', { actionId }, data))
      },
      invalidatesTags: (_result, _error, { actionId }) => [actionTag(actionId)]
    }
  }
})

const pagedConversationsApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    listConversationPages: build.infiniteQuery<CursorPagedListConversation, void, string | null>({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) => (lastPage.meta.last === false ? (lastPage.data.at(-1)?.id ?? null) : null)
      },
      query: ({ pageParam }) => ({
        url: '/api/v1/assistant/conversations',
        params: { limit: CONVERSATION_PAGE_SIZE, starting_after: pageParam ?? undefined }
      }),
      providesTags: [CONVERSATION_LIST]
    })
  }),
  overrideExisting: false
})

export const { useListConversationPagesInfiniteQuery } = pagedConversationsApi

/** Refreshes the conversation history, as after a turn finishes streaming outside RTK Query. */
export const invalidateConversationList = () => enhancedApi.util.invalidateTags([CONVERSATION_LIST])

export type { Action as AssistantAction, Conversation as AssistantConversation }
